import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, SearchBar } from '@/components';
import { useTranslation } from 'react-i18next';
import { useSearchParamsAsObject } from '@/hooks/useSearchParamsAsObject.hook';
import { useProfile } from '@/hooks';
import {
  useClanControllerCreateJoinRequestMutation,
  useClanControllerFindOneQuery,
  useProfileControllerSearchQuery,
} from '@/store/services/MidleNestApi.service';

export default function ClanInvites() {
  const { t } = useTranslation();

  const { profile } = useProfile();
  const { data: myClan, isLoading } = useClanControllerFindOneQuery(profile?.clan?.id ?? '');

  const navigate = useNavigate();
  const [search, setSearch] = React.useState<string | undefined>();
  const { searchParams } = useSearchParamsAsObject<{
    page: number;
    limit: number;
  }>();
  const { page = 1, limit = 10 } = searchParams;
  const { data } = useProfileControllerSearchQuery({ page, limit, search });
  const [invite] = useClanControllerCreateJoinRequestMutation();
  const { pathname } = useLocation();

  const guildId = React.useMemo(() => pathname.split('/')[2], [pathname]);

  const inviteUserHandler = React.useCallback(
    async (userId: string) => {
      await invite({
        requester: 'clan',
        message: 'null',
        clanId: guildId,
        userId,
      }).unwrap();
    },
    [invite],
  );

  if (myClan?.id !== guildId) return <Navigate to={'/clans/' + guildId} />;

  return (
    <div className='flex flex-col gap-y-8 p-4'>
      <h3 className='text-lg font-medium text-center leading-7'>{t('Invite Members')}</h3>
      <div className='flex flex-col gap-y-4 items-center'>
        <SearchBar onDebouncedUpdate={setSearch} />
        <table className='table-fixed w-full'>
          <thead>
            <tr className='font-semibold text-xs sm:text-sm text-center'>
              <td className='text-left'>{t('Member')}</td>
              <td className=''>{t('Avg. Points per Day')}</td>
              <td className='' />
            </tr>
          </thead>
          <tbody>
            {data?.list?.map((i, k) => (
              <tr key={i.id} className='text-xs sm:text-sm text-center'>
                <td className='py-3 text-left'>
                  <div className='flex gap-x-2 items-center'>
                    <Avatar
                      name={i.user.username as string}
                      src={i.avatar?.src}
                      className='hidden sm:flex w-8 h-8'
                    />
                    <span className='max-w-[140px] truncate'>{i.user.username}</span>
                  </div>
                </td>
                <td className=''>{i.currentXp}</td>
                <td className='text-right'>
                  <button
                    className='px-4 py-1 rounded-md bg-blue-500 text-white disabled:grayscale disabled:cursor-not-allowed'
                    type='button'
                    onClick={() => inviteUserHandler(i.id as string)}
                    disabled={myClan?.invitedIds?.includes(i.id)}
                  >
                    {myClan?.invitedIds?.includes(i.id) ? t('Invited!') : t('Invite')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        {/*TODO: <Pagination page={page} limit={limit} totalPage={data?.meta?.total} maxSiblings={1} />*/}
      </div>

      <button
        type='button'
        className='absolute right-4 top-4 text-gray-500 text-lg'
        onClick={() => navigate('/clans/' + guildId)}
      >
        <i className='icon-close' />
      </button>
    </div>
  );
}
